import React, { useEffect, useRef, useState } from 'react';
import { PortalShell } from '@/components/nav/PortalShell';
import { portalApi, uploadApi, uploadUrl } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Send, Camera, X } from 'lucide-react';
import { fmtDate } from '@/lib/format';
import { toast } from 'sonner';
import { Skeleton } from '@/components/ui/skeleton';
import { PORTAL } from '@/constants/testIds';

export default function PortalMessagesPage() {
  const [messages, setMessages] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [text, setText] = useState('');
  const [photoIds, setPhotoIds] = useState([]);
  const [photoUrls, setPhotoUrls] = useState([]);
  const scrollRef = useRef(null);

  const load = () => portalApi.messages()
    .then(setMessages)
    .catch(() => {}) // surfaced by the api response interceptor
    .finally(() => setLoaded(true));
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages]);

  const attach = async (file) => {
    try {
      const up = await uploadApi.upload(file);
      setPhotoIds(ids => [...ids, up.id]);
      setPhotoUrls(urls => [...urls, up.url]);
    } catch (e) { toast.error('Upload failed'); }
  };

  const send = async () => {
    if (!text.trim() && photoIds.length === 0) return;
    try {
      await portalApi.sendMessage(text, photoIds);
      setText(''); setPhotoIds([]); setPhotoUrls([]);
      load();
    } catch (e) { toast.error('Send failed'); }
  };

  return (
    <PortalShell title="Messages">
      <h1 className="font-display text-2xl md:text-3xl font-semibold mb-3">Messages</h1>
      <Card><CardContent className="p-4 flex flex-col h-[65vh]">
        <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-2 pb-3">
          {!loaded ? (
            <div className="space-y-2">
              <Skeleton className="h-12 w-2/3 rounded-lg" />
              <Skeleton className="h-12 w-1/2 rounded-lg ml-auto" />
              <Skeleton className="h-12 w-3/5 rounded-lg" />
            </div>
          ) : messages.length === 0 ? <div className="text-sm text-muted-foreground text-center py-8">Start the conversation — our team will reply here.</div> :
            messages.map(m => (
              <div key={m.id} className={`max-w-[80%] rounded-lg px-3 py-2 ${m.direction === 'inbound' ? 'ml-auto bg-primary/10' : 'bg-muted'}`}>
                <div className="text-sm whitespace-pre-wrap break-words">{m.body}</div>
                {m.photo_ids && m.photo_ids.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {m.photo_ids.map(pid => (
                      <img key={pid} src={uploadUrl(`/uploads/${pid}.jpg`)} alt="attachment" className="h-24 w-24 object-cover rounded-md border" onError={(e) => { e.target.style.display = 'none'; }} />
                    ))}
                  </div>
                )}
                <div className="text-[10px] text-muted-foreground mt-1">{m.channel} • {fmtDate(m.created_at)}</div>
              </div>
            ))
          }
        </div>
        {photoUrls.length > 0 && (
          <div className="flex gap-2 pb-2 flex-wrap">
            {photoUrls.map((u, i) => (
              <div key={i} className="relative">
                <img src={uploadUrl(u)} alt="pending" className="h-16 w-16 object-cover rounded-md border" />
                <button type="button" onClick={() => { setPhotoIds(ids => ids.filter((_, x) => x !== i)); setPhotoUrls(us => us.filter((_, x) => x !== i)); }} className="absolute -top-1 -right-1 rounded-full bg-background border p-0.5"><X className="h-3 w-3" /></button>
              </div>
            ))}
          </div>
        )}
        <div className="pt-3 border-t flex gap-2 items-end">
          <Textarea value={text} onChange={e => setText(e.target.value)} placeholder="Type a message…" className="flex-1" rows={2} data-testid={PORTAL.messageInput} />
          <label className="cursor-pointer">
            <input type="file" accept="image/*" className="hidden" data-testid={PORTAL.messagePhoto} onChange={e => e.target.files?.[0] && attach(e.target.files[0])} />
            <Button asChild variant="outline" size="icon" type="button"><span><Camera className="h-4 w-4" /></span></Button>
          </label>
          <Button onClick={send} data-testid={PORTAL.messageSend}><Send className="h-4 w-4" /></Button>
        </div>
      </CardContent></Card>
    </PortalShell>
  );
}
